// frontend/src/store/sudoStore.js
//
// Zustand store mirroring the backend's `user_sudo_grants` for the currently
// authenticated user. Updated on:
//   1. App boot — useSudoStatus() hook polls GET /auth/step-up/status once
//   2. Successful POST /auth/step-up — StepUpModal calls setActive(grant)
//   3. POST /auth/step-up/lock — SudoBanner's "Lock now" button → clear()
//   4. 401 STEP_UP_REQUIRED — api.js interceptor clears the local state
//      (the server already invalidated/never had a grant).
//
// `expiresAt` is an ISO string; the countdown is derived in useSudoCountdown.

import { create } from 'zustand'

/** A backend sudo grant, as returned by GET/POST /auth/step-up[/status]. */
export interface SudoGrant {
  expires_at?: string | null
  scopes?: string[]
}

export interface SudoState {
  active: boolean
  /** ISO 8601 string while a grant is active, else null. */
  expiresAt: string | null
  scopes: string[]
  setActive: (grant: SudoGrant) => void
  clear: () => void
  hasScope: (scope: string) => boolean
}

const useSudoStore = create<SudoState>((set) => ({
  active: false,
  expiresAt: null,   // ISO 8601 string
  scopes: [],

  setActive: ({ expires_at, scopes }) => set({
    active: true,
    expiresAt: expires_at || null,
    scopes: Array.isArray(scopes) ? scopes : [],
  }),

  clear: () => set({ active: false, expiresAt: null, scopes: [] }),

  hasScope: (scope) => {
    const s: SudoState = useSudoStore.getState()
    if (!s.active) return false
    if (!s.expiresAt) return false
    if (Date.parse(s.expiresAt) <= Date.now()) return false
    return s.scopes.includes(scope)
  },
}))

export default useSudoStore
